/**
 * client/src/components/layout/invitation-push-listener.tsx
 *
 * Mounts the invitation listener once, inside the authenticated app shell, so a
 * notice reaches the person on whatever page they happen to be on (#2171) — not
 * only if they are already looking at Invitations. Covers all three rails: the
 * two org events and `guardian:invite_received`.
 *
 * Two effects on arrival:
 *   1. Invalidate BOTH inbox queries, so the nav badge and the Invitations page
 *      update without a refresh. Uses the shared keys, imported rather than
 *      retyped, so a rename breaks the build instead of silently no-op-ing.
 *   2. Raise a toast. Success-variant styling: neutral elevated card, not
 *      amber — an invitation is not an error (CLAUDE.md Design Constraints
 *      reserve amber for user errors/dormancy).
 *
 * Renders nothing. It is a listener, deliberately not a UI component: a
 * WebSocket notice reaches a LIVE session only, so it can never be the thing a
 * person relies on — the badge is. Nothing here claims delivery.
 */

import { useQueryClient } from "@tanstack/react-query";

import { toast } from "@/components/ui/toast";
import {
  CONNECTION_REQUESTS_QUERY_KEY,
  GUARDIANSHIPS_QUERY_KEY,
  MEMBERSHIP_REQUESTS_QUERY_KEY,
} from "@/lib/pending-invitations";
import { describeInvitation, useInvitationPush } from "@/lib/use-invitation-push";

export function InvitationPushListener(): null {
  const queryClient = useQueryClient();

  useInvitationPush((frame) => {
    // Every key, not just the matching one: the frame's `kind` decides which
    // list actually grew, but refetching the others costs one cached read each
    // and removes a whole class of "badge is stale because the kind mapping
    // drifted" bug.
    void queryClient.invalidateQueries({ queryKey: MEMBERSHIP_REQUESTS_QUERY_KEY });
    void queryClient.invalidateQueries({ queryKey: CONNECTION_REQUESTS_QUERY_KEY });
    void queryClient.invalidateQueries({ queryKey: GUARDIANSHIPS_QUERY_KEY });
    toast(describeInvitation(frame));
  });

  return null;
}
